import axios from 'axios'
import { Mixed } from 'common-types'
import {
    AUTH_CLIENT_HEADER,
    WEB_AUTH_CLIENT,
    clearStoredSession,
    getStoredToken,
    refreshAccessToken,
} from '@/lib/api/session-refresh'
import { requestStepUp } from '@/lib/api/step-up'
import { authTabHeaders } from '@/lib/auth-tab-session'

declare global {
    interface Window {
        __atendimento_logging_out?: boolean
    }
}

declare module 'axios' {
    // Marcam a requisição já repetida, para não entrar em laço.
    export interface InternalAxiosRequestConfig {
        __isRetryAfterRefresh?: boolean
        __isRetryAfterStepUp?: boolean
    }
}

/**
 * Motivos de recusa que a renovação silenciosa resolve: a credencial venceu ou
 * não chegou. Nenhum deles derruba a sessão — quem decide isso é a renovação,
 * ao falhar.
 */
const RENEWABLE_AUTH_CODES = [
    'TOKEN_EXPIRED',
    'TOKEN_MISSING',
    'USER_NOT_AUTHENTICATED',
]

export const getClient = (formData: boolean = false) => {
    const baseURL = import.meta.env.VITE_API_BASE_URL

    if (process.env.NODE_ENV === 'development') {
        console.log('[API Client] Base URL:', baseURL)
    }

    if (!baseURL) {
        console.error('[API Client] REACT_APP_API_BASE_URL não está definida!')
    }

    const axiosHeaders: Record<string, string> = {}

    if (!formData) {
        axiosHeaders['Content-Type'] = 'application/json'
    }



    const additionalHeaders: Mixed = {}

    const client = axios.create({
        baseURL,
        headers: {
            ...additionalHeaders,
            ...axiosHeaders,
        },
        params: {
            //lng: 'pt',
        },
        withCredentials: true,
        maxContentLength: 50 * 1024 * 1024, // 50MB
        maxBodyLength: 50 * 1024 * 1024, // 50MB
        timeout: 300000, // 5 minutos para uploads grandes

    })

    client.interceptors.request.use(config => {
        const token = getStoredToken()

        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }

        // Pede ao backend o modo cookie: o refresh volta em cookie httpOnly, e
        // não no corpo da resposta.
        config.headers[AUTH_CLIENT_HEADER] = WEB_AUTH_CLIENT

        // Id da aba (e o escopo, na aba de usuário de IA): o backend guarda a sessão da IA
        // num cookie desta aba, ao lado da sessão normal (ver `auth-tab-session.ts`).
        Object.assign(config.headers, authTabHeaders())

        return config
    })

    client.interceptors.response.use(
        response => response,
        async error => {
            if (error.response && error.response.status === 429) {
                error.response.data = {
                    data: null,
                    message:
                        'Você está indo muito rápido, aguarde alguns instantes!',
                }
            }

            const authStatus = error.response?.status
            const code = error.response?.data?.code

            // Token expirado é o caso normal de sessão em uso: renova por baixo
            // e repete a requisição, sem o usuário perceber. Só se a renovação
            // falhar é que a sessão cai.
            // TOKEN_MISSING entra junto: recarregar a página deixa a memória
            // sem access token, e a requisição que sair antes da renovação
            // inicial chega ao backend sem credencial nenhuma.
            // USER_NOT_AUTHENTICATED é o mesmo caso visto pelo `PermissionGuard`:
            // a requisição chegou sem usuário resolvido. Vem como 403, e enquanto
            // não era tratado aqui a tela simplesmente ficava vazia — sem renovar
            // e sem deslogar — até o usuário apertar F5.
            if (
                RENEWABLE_AUTH_CODES.includes(code) &&
                (authStatus === 401 || authStatus === 403) &&
                error.config &&
                !error.config.__isRetryAfterRefresh &&
                !window.__atendimento_logging_out
            ) {
                // O token que esta requisição usou: se ele já não é o da
                // memória, outra requisição (ou outra aba) renovou enquanto
                // esta viajava, e basta repetir com o novo.
                const usedToken =
                    typeof error.config.headers?.Authorization === 'string'
                        ? error.config.headers.Authorization.replace(
                              /^Bearer\s+/,
                              ''
                          )
                        : null

                const newToken = await refreshAccessToken(usedToken)

                if (newToken) {
                    error.config.__isRetryAfterRefresh = true
                    error.config.headers.Authorization = `Bearer ${newToken}`

                    return client.request(error.config)
                }
            }

            // Ação de dinheiro que exige senha recente: pede a confirmação e
            // repete a requisição. A tela que disparou a ação não precisa saber
            // de nada — quem marca a rota como sensível é o backend.
            if (
                authStatus === 403 &&
                code === 'STEP_UP_REQUIRED' &&
                error.config &&
                !error.config.__isRetryAfterStepUp
            ) {
                const confirmed = await requestStepUp()

                if (confirmed) {
                    error.config.__isRetryAfterStepUp = true

                    return client.request(error.config)
                }
            }

            // Sessão inválida: limpa a sessão e volta ao login. Cobre 401 e 403
            // (o guard usa ambos). Sem TOKEN_VERSION_MISMATCH/USER_NOT_FOUND aqui,
            // um usuário cuja unidade/empresa foi alterada (o update incrementa o
            // tokenVersion) ficava preso na sessão antiga — não conseguia logar.
            if (authStatus === 401 || authStatus === 403) {
                // TOKEN_MISSING não entra aqui: ele é tratado acima pela
                // renovação, e só chega neste ponto se ela já falhou.
                const SESSION_LOGOUT_CODES = [
                    'SESSION_INVALIDATED',
                    'TOKEN_EXPIRED',
                    'INVALID_TOKEN',
                    // Token sem o claim `type` (emitido antes da separação
                    // access/refresh) ou refresh usado como credencial.
                    'INVALID_TOKEN_TYPE',
                    'TOKEN_VERSION_MISMATCH',
                    'USER_NOT_FOUND',
                    'ACCOUNT_BLOCKED',
                    'ACCOUNT_INACTIVE',
                ]
                if (SESSION_LOGOUT_CODES.includes(code)) {
                    // Evitar múltiplos logouts simultâneos
                    if (!window.__atendimento_logging_out) {
                        window.__atendimento_logging_out = true
                        clearStoredSession()
                        window.location.href = '/'
                    }
                }
            }

            return Promise.reject(error)
        }
    )

    return client
}
